import React, { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
	ChildRows,
	type Props as ChildRowsProps,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows/index.tsx';
import { ChildRowsLoader } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows-loader/ChildRowsPreloadedQuery.tsx';
import type {
	RenderChildRowsArgs,
	RenderCreateIssueController,
	RenderDraggableWrapper,
	RenderDraggableWrapperProps,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';
import type { RenderTableCell } from '@atlassian/jira-issue-table-hierarchy/src/ui/loading-row/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Rows from '../../../rows/Rows.tsx';
import {
	CHILD_ITEMS_EXPERIENCE,
	CHILD_ITEMS_LOAD_MORE_EXPERIENCE,
	ANALYTICS_SOURCE,
	MAX_COLUMNS,
	ACTION_COLUMNS,
	CHILD_QUERY_PAGE_SIZE,
} from '../../../../common/constants.tsx';
import { useShouldApplyFrozenColumns } from '../../../../controllers/use-should-apply-frozen-columns/index.tsx';
import {
	useIsReparentingEnabled,
	useSortDirection,
	useSortField,
	useJql,
	useIsFixedTableLayoutEnabled,
	useIsIssueRankDescending,
	useIsHideDoneItemsEnabled,
	useIsTimelineEnabled,
	useIsInlineFieldConfigEnabled,
	useIsConditionalFormattingEnabled,
	useIsRelationshipFieldEnabled,
	useCellBorderStyle,
	useIsRemoteLinkedIssuesEnabled,
	useIsEditableSyncLoaded,
	useIsStickyColumnsVisible,
	useIsInlineEditingEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { TableCell } from '../../../../common/ui/table-cell/index.tsx';
import { useScrollStateSelector } from '../../../../controllers/scroll-state/index.tsx';
import { CreateIssueController } from '../../../../services/issue-create-mutation/CreateIssueController.tsx';
import { DraggableRowsContainer } from '../../../../controllers/draggable-rows/context.tsx';
import type { Column, RankFieldTerms } from '../../../../common/types.tsx';
import { useTimelineCustomComponents } from '../../../../controllers/timeline-custom-components/index.tsx';
import type { RenderChildRowsComp_rules$key } from './__generated__/RenderChildRowsComp_rules.graphql';
import type { RenderChildRowsComp$key } from './__generated__/RenderChildRowsComp.graphql';
import type { RenderChildRowsComp_rank$key } from './__generated__/RenderChildRowsComp_rank.graphql';

type RenderChildRowsCompProps = RenderChildRowsArgs & {
	projectKey?: string;
	isGroupingSupported?: boolean;
	columns: Column[];
	project: RenderChildRowsComp$key | null | undefined;
	rankField: RenderChildRowsComp_rank$key | null;
	formattingRules?: RenderChildRowsComp_rules$key | null | undefined;
	rowInlineCreateCanShowAdditionalFields: boolean;
};

const renderDraggableWrapper: RenderDraggableWrapper = (props: RenderDraggableWrapperProps) => {
	return (
		<DraggableRowsContainer
			issueConnectionId={props.issueConnectionId}
			instanceId={props.instanceId}
		>
			{props.children}
		</DraggableRowsContainer>
	);
};

export const RenderChildRowsComp = ({
	project,
	projectKey,
	isGroupingSupported,
	columns,
	rankField,
	formattingRules,
	rowInlineCreateCanShowAdditionalFields,
	...childRowsProps
}: RenderChildRowsCompProps) => {
	const ChildIssueCreateRowComponent = useTimelineCustomComponents()?.ChildIssueCreateRow;
	let isStickyColumnsVisible = false;
	if (fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isStickyColumnsVisible = useIsStickyColumnsVisible();
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { isStickyColumnsVisible: isStickyColumnsVisibleOld } = useShouldApplyFrozenColumns();
		isStickyColumnsVisible = isStickyColumnsVisibleOld;
	}

	const renderTableCell: RenderTableCell = (tableCellProps) => <TableCell {...tableCellProps} />;

	const projectRef = useFragment<RenderChildRowsComp$key>(
		graphql`
			fragment RenderChildRowsComp on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...Rows_project @arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const rankFieldData = useFragment<RenderChildRowsComp_rank$key>(
		graphql`
			fragment RenderChildRowsComp_rank on JiraJqlFieldWithAliases
			@argumentDefinitions(
				isJSCAliasChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-alias-changes.relayprovider"
				}
			) {
				fieldId
				jqlTerm
				aliases @include(if: $isJSCAliasChangesEnabled)
			}
		`,
		rankField,
	);

	const formattingRulesData = useFragment<RenderChildRowsComp_rules$key>(
		graphql`
			fragment RenderChildRowsComp_rules on JiraFormattingRuleConnection {
				...Rows_rules
			}
		`,
		formattingRules,
	);

	/**
	 * Passing those values down to ChildRowsLoader as props to avoid direct imports
	 */
	const jql = useJql();
	const sortField = useSortField();
	const sortDirection = useSortDirection();
	const isReparentingEnabled = useIsReparentingEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isHideDoneItemsEnabled = useIsHideDoneItemsEnabled();
	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const width = useScrollStateSelector((scrollState) => scrollState.width);
	const isIssueRankDescending = useIsIssueRankDescending();
	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
	const isLinkRelationshipFieldEnabled = useIsRelationshipFieldEnabled();
	const isRemoteLinkedIssuesEnabled = useIsRemoteLinkedIssuesEnabled();
	const isEditableSyncLoaded = useIsEditableSyncLoaded();
	const cellBorderStyle = useCellBorderStyle();
	const isInlineEditingEnabled = fg('jira_timeline_disable_child_inline_editing')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsInlineEditingEnabled()
		: true;

	const isConditionalFormattingEnabled =
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fg('jira_list_conditional_formatting') && useIsConditionalFormattingEnabled();

	const rankFieldTerms = useMemo<RankFieldTerms>(
		() => ({
			jqlTerm: rankFieldData?.jqlTerm ?? null,
			aliases: rankFieldData?.aliases ? Array.from(rankFieldData.aliases) : null,
		}),
		[rankFieldData],
	);

	const renderCreateIssueController = useCallback<RenderCreateIssueController>(
		(props) => <CreateIssueController {...props} jql={jql} rankFieldTerms={rankFieldTerms} />,
		[jql, rankFieldTerms],
	);

	const renderChildRowsLoader = useCallback<ChildRowsProps['renderChildRowsLoader']>(
		(childRowsLoaderProps) => (
			<ChildRowsLoader
				{...childRowsLoaderProps}
				jql={jql}
				width={width}
				sortField={sortField}
				sortDirection={sortDirection}
				columns={columns}
				amountOfColumns={MAX_COLUMNS}
				actionColumns={ACTION_COLUMNS}
				pageSize={CHILD_QUERY_PAGE_SIZE}
				isReparentingEnabled={isReparentingEnabled}
				isTimelineEnabled={isTimelineEnabled}
				isIssueRankDescending={isIssueRankDescending}
				isHideDoneItemsEnabled={isHideDoneItemsEnabled}
				isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
				isConditionalFormattingEnabled={isConditionalFormattingEnabled}
				rankFieldId={rankFieldData?.fieldId}
				renderRows={(rowProps) => (
					<Rows
						project={projectRef}
						formattingRules={formattingRulesData}
						rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
						{...rowProps}
					/>
				)}
				renderDraggableWrapper={renderDraggableWrapper}
				renderTableCell={renderTableCell}
				isStickyColumnsVisible={isStickyColumnsVisible}
				renderCreateIssueController={renderCreateIssueController}
				isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
				ChildIssueCreateRowComponent={ChildIssueCreateRowComponent}
				isLinkRelationshipFieldEnabled={isLinkRelationshipFieldEnabled}
				isRemoteLinkedIssuesEnabled={isRemoteLinkedIssuesEnabled}
				isEditableSyncLoaded={isEditableSyncLoaded}
				cellBorderStyle={cellBorderStyle}
				isInlineEditingEnabled={isInlineEditingEnabled}
			/>
		),
		[
			jql,
			width,
			sortField,
			sortDirection,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			isIssueRankDescending,
			isHideDoneItemsEnabled,
			isFixedTableLayoutEnabled,
			isConditionalFormattingEnabled,
			rankFieldData?.fieldId,
			isStickyColumnsVisible,
			renderCreateIssueController,
			isInlineFieldConfigEnabled,
			ChildIssueCreateRowComponent,
			isLinkRelationshipFieldEnabled,
			isRemoteLinkedIssuesEnabled,
			isEditableSyncLoaded,
			cellBorderStyle,
			isInlineEditingEnabled,
			projectRef,
			formattingRulesData,
			rowInlineCreateCanShowAdditionalFields,
		],
	);

	return (
		<ChildRows
			{...childRowsProps}
			projectKey={projectKey}
			isGroupingSupported={isGroupingSupported}
			childItemsExperience={CHILD_ITEMS_EXPERIENCE}
			childItemsLoadMoreExperience={CHILD_ITEMS_LOAD_MORE_EXPERIENCE}
			analyticsSource={ANALYTICS_SOURCE}
			renderChildRowsLoader={renderChildRowsLoader}
		/>
	);
};
